import { inject, Injectable } from '@angular/core';
import { Observable, switchMap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { TicketClientApiService } from 'src/app/core/service/apis/tickets/ticket-client-api.service';
import {
  TicketField,
  toTicketField,
  toTicketFieldValue,
  toTicketMetadataKey,
} from 'src/app/shared/constants/ticket-field.constants';

/**
 * Decide cómo se escribe un campo de ticket: columna propia, clave dentro de `metadata`, o
 * ninguna. Compartido por los 5 repositorios de ticket.
 */
@Injectable({ providedIn: 'root' })
export class TicketFieldUpdateService {
  private readonly api = inject(TicketClientApiService);

  updateField(recordId: string, field: string, value: unknown): Observable<void> {
    const metadataKey = toTicketMetadataKey(field);
    if (metadataKey) return this.updateMetadataKey(recordId, metadataKey, value);

    const ticketField = toTicketField(field);
    if (!ticketField) {
      return throwError(() => new Error(`Campo no soportado por update/field/tickets: ${field}`));
    }

    return this.api.updateTicketField(recordId, ticketField, toTicketFieldValue(ticketField, value)).pipe(
      map(() => undefined),
    );
  }

  /**
   * `metadata` se escribe entero, así que primero se lee el objeto actual y se
   * mezcla la clave editada — mandar solo esa clave borraría las demás.
   */
  private updateMetadataKey(recordId: string, key: string, value: unknown): Observable<void> {
    return this.api.getTicketDetail(recordId, { silent: true }).pipe(
      switchMap(detail => this.api.updateTicketField(
        recordId,
        TicketField.METADATA,
        { ...detail?.metadata, [key]: value },
      )),
      map(() => undefined),
    );
  }
}
